/**
 * Gestor de notificaciones y animación en la pestaña del explorador (document.title y favicon).
 * Muestra alertas sutiles cuando llega un nuevo mensaje y el usuario está en otra pestaña o ventana.
 */

let baseTitle = typeof document !== 'undefined' ? document.title : 'Akasia Admin Panel';
let notificationInterval = null;
let unreadCount = 0;
let currentAlertText = '¡Nuevo mensaje!';
let currentDetailText = '';
let isToggleOn = false;
let originalFaviconHref = null;

/**
 * Dibuja o remueve un punto indicador rojo en el favicon del explorador
 */
export const drawFaviconBadge = (hasNotification) => {
  try {
    if (typeof document === 'undefined') return;
    const favicon = document.querySelector("link[rel*='icon']");
    if (!favicon) return;

    if (!originalFaviconHref) {
      originalFaviconHref = favicon.href;
    }

    if (!hasNotification) {
      if (favicon.href !== originalFaviconHref) {
        favicon.href = originalFaviconHref;
      }
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = originalFaviconHref;

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 32;
        canvas.height = 32;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 32, 32);

        // Círculo rojo de notificación con borde blanco en esquina superior derecha
        ctx.beginPath();
        ctx.arc(24, 8, 6.5, 0, 2 * Math.PI);
        ctx.fillStyle = '#ef4444';
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();

        favicon.href = canvas.toDataURL('image/png');
      } catch {
        // Fallback silencioso en caso de restricciones de canvas
      }
    };
    img.onerror = () => {
      // Fallback silencioso
    };
  } catch {
    // Fallback silencioso
  }
};

/**
 * Genera un sonido sutil y nítido de notificación usando Web Audio API sin dependencias externas
 */
export const playChimeSound = () => {
  try {
    const AudioContextClass = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    const now = ctx.currentTime;
    osc.frequency.setValueAtTime(587.33, now); // Nota D5
    osc.frequency.setValueAtTime(880, now + 0.12); // Nota A5

    gain.gain.setValueAtTime(0.12, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.38);
  } catch {
    // Fallback silencioso si audio bloqueado por navegador
  }
};

/**
 * Define el título base de la pestaña actual (por ejemplo: "Ticket #12 · Juan Pérez | Akasia Support")
 */
export const setTabBaseTitle = (title) => {
  if (!title) return;
  baseTitle = title;
  if (!notificationInterval && typeof document !== 'undefined') {
    document.title = title;
  }
};

export const getTabBaseTitle = () => baseTitle;

/**
 * Activa la animación sutil en la pestaña del explorador alternando el texto de nuevo mensaje
 */
export const triggerTabNotification = ({ alertText, detailText, count } = {}) => {
  if (typeof document === 'undefined') return;

  unreadCount = count !== undefined ? count : unreadCount + 1;
  if (alertText) currentAlertText = alertText;
  if (detailText) currentDetailText = detailText;

  drawFaviconBadge(true);

  if (notificationInterval) return;

  isToggleOn = true;
  const countLabel = `(${unreadCount})`;
  document.title = `🔔 ${countLabel} ${currentAlertText}`;

  notificationInterval = setInterval(() => {
    isToggleOn = !isToggleOn;
    const currentCountLabel = `(${unreadCount})`;

    if (isToggleOn) {
      document.title = `🔔 ${currentCountLabel} ${currentAlertText}`;
    } else {
      document.title = currentDetailText
        ? `💬 ${currentCountLabel} ${currentDetailText}`
        : `💬 ${currentCountLabel} ${baseTitle}`;
    }
  }, 1100);
};

/**
 * Detiene la animación y restaura el título y favicon originales
 */
export const clearTabNotification = () => {
  if (notificationInterval) {
    clearInterval(notificationInterval);
    notificationInterval = null;
  }
  unreadCount = 0;
  isToggleOn = false;
  drawFaviconBadge(false);

  if (typeof document !== 'undefined' && baseTitle) {
    document.title = baseTitle;
  }
};

// Escuchadores globales para limpiar la notificación en cuanto el usuario vuelve a la pestaña o hace clic
if (typeof window !== 'undefined') {
  const handleUserActive = () => {
    if (typeof document !== 'undefined' && !document.hidden) {
      clearTabNotification();
    }
  };

  window.addEventListener('focus', handleUserActive);
  window.addEventListener('click', handleUserActive);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      clearTabNotification();
    }
  });
}
